import { MenuItem } from "@/app/ui/navigation/types";
import type Guid from "@/app/types/common/guid";
import type { Location, Route } from "vue-router";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

export const contactsMenu = {
  root: "navigation_contacts_group",
  // Specify correct value for isRetail if known. However it will redirect as appropriate.
  viewOutlet: (
    outletId: Guid,
    isRetail: boolean = false,
    taskId: Guid | null = null
  ): Location => {
    if (taskId) {
      return {
        name: isRetail
          ? contactsMenu.retailOutlets.crm_task
          : contactsMenu.outlets.crm_task,
        params: { id: outletId.toString(), taskId: taskId.toString() },
      };
    }
    return {
      name: isRetail
        ? contactsMenu.retailOutlets.crm
        : contactsMenu.outlets.crm,
      params: { id: outletId.toString() },
    };
  },
  duplicateOutlet: (outletId: Guid): Location => ({
    name: contactsMenu.outlets.create,
    query: { duplicate_from_outlet_id: outletId.toString() },
  }),
  outlets: {
    create: "create_outlet",
    createLocation: (opts?: {
      leadId?: Guid;
      sellarCustomerId?: number;
      sellarSettingsId?: Guid;
    }): Location => ({
      name: contactsMenu.outlets.create,
      query: {
        create_from_lead: opts?.leadId?.toString(),
        create_from_sellar_customer: opts?.sellarCustomerId?.toString(),
        create_from_sellar_settings: opts?.sellarSettingsId?.toString(),
      },
    }),
    createOutletFromLeadsMap: (opts: {
      businessName: string;
      telephoneNumber: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      addressLine4: string;
      addressLine5: string;
      addressLine6: string;
      addressPostcode: string;
      webAddress: string;
    }): Location => ({
      name: contactsMenu.outlets.create,
      query: opts,
    }),

    list: "outlet_list",
    view: "outlet",
    salesHistory: "outlet_sales_history",
    crm_task: "outlet_crm_task",
    crm: "outlet_crm",
    contacts: "outlet_contacts",
    containerHistory: "outlet_containerHistory",
    documents: "outlet_documents",
    deposits: "outlet_deposits",
    pricelists: {
      root: "outlet_price_lists",
    },
    finance: "outlet_finance",
    settings: "outlet_settings",
    containerHistoryLocation: (id: Guid): Location => ({
      name: contactsMenu.outlets.containerHistory,
      params: { id: id.toString() },
    }),
  },
  retailOutlets: {
    view: "retail_outlet",
    salesHistory: "retail_outlet_sales_history",
    crm: "retail_outlet_crm",
    crm_task: "retail_outlet_crm_task",
    documents: "retail_outlet_documents",
    deposits: "retail_outlet_deposits",
    settings: "retail_outlet_settings",
  },
  outletGroups: {
    list: "group_list",
    create: "create_outlet_group",
    view: "group_settings",
    contacts: "group_contacts",
    documents: "group_documents",
    outlets: "group_outlets",
    pricing: "group_pricing",
    finance: "group_finance",
    settings: "group_settings",
  },
  viewOutletGroup: (id: Guid): Location => ({
    name: contactsMenu.outletGroups.view,
    params: { id: id.toString() },
  }),
  suppliers: {
    list: "supplier_list",
    view: "supplier_view",
    create: "supplier_create",
    createLocation: (): Location => ({
      name: contactsMenu.suppliers.create,
    }),
    viewLocation: (id: Guid, isEditing: boolean): Location => ({
      name: contactsMenu.suppliers.view,
      params: { id: id.toString(), mode: isEditing ? "edit" : "view" },
    }),
  },
  viewSupplier: (id: Guid): Location =>
    contactsMenu.suppliers.viewLocation(id, false),
  communications: {
    list: "communications_list",
    listLocation: (): Location => ({
      name: contactsMenu.communications.list,
    }),
  },
};

export const buildContactsMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: contactsMenu.root,
    icon: "Contacts",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuContactsView]),
    subMenus: [
      MenuItem.fromOpts({
        name: contactsMenu.outlets.list,
        icon: "List",
        url: "/outlets/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/ListOutlets.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.outlets.create,
        icon: "Create",
        url: "/outlets/create",
        breadcrumbParentName: contactsMenu.outlets.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/CreateOutletWizard.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.retailOutlets.view,
        icon: "Edit",
        url: "/retailoutlet/:id/",
        breadcrumbParentName: contactsMenu.outlets.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/ViewRetailOutlet.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          id: route.params.id,
        }),
        routeMetadata: {
          routeKey: (route) => "/retailoutlet/" + route.params.id,
        },
        childRoutes: [
          {
            name: contactsMenu.retailOutlets.salesHistory,
            path: "saleshistory",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/OutletSalesHistoryTab.vue"
              ),
            meta: {
              requiredUserPermissions: [EnumUserPermission.ordersView],
            },
          },
          {
            name: contactsMenu.retailOutlets.crm_task,
            path: "crm/task/:taskId",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/crm/CrmTab.vue"
              ),
            props: (route) => ({
              taskId: route.params.taskId,
            }),
          },
          {
            name: contactsMenu.retailOutlets.crm,
            path: "crm",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/crm/CrmTab.vue"
              ),
          },
          {
            name: contactsMenu.retailOutlets.documents,
            path: "documents",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/DocumentStoreTab.vue"
              ),
          },
          {
            name: contactsMenu.retailOutlets.deposits,
            path: "deposits",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/OutletDepositsTab.vue"
              ),
          },
          {
            name: contactsMenu.retailOutlets.settings,
            path: "settings",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewretailoutlet/RetailOutletSettingsTab.vue"
              ),
          },
        ],
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.outlets.view,
        icon: "Edit",
        url: "/outlet/:id/",
        breadcrumbParentName: contactsMenu.outlets.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/ViewOutlet.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          id: route.params.id,
        }),
        routeMetadata: {
          routeKey: (route: Route) => "/outlet/" + route.params.id,
        },
        childRoutes: [
          {
            name: contactsMenu.outlets.salesHistory,
            path: "saleshistory",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/OutletSalesHistoryTab.vue"
              ),
            meta: {
              requiredUserPermissions: [EnumUserPermission.ordersView],
            },
          },
          {
            name: contactsMenu.outlets.crm_task,
            path: "crm/task/:taskId",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/crm/CrmTab.vue"
              ),
            props: (route) => ({
              taskId: route.params.taskId,
            }),
          },
          {
            name: contactsMenu.outlets.crm,
            path: "crm",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/crm/CrmTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.contacts,
            path: "contacts",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/contacts/ContactsTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.containerHistory,
            path: "container-history",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/OutletContainerHistoryTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.documents,
            path: "documents",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/DocumentStoreTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.deposits,
            path: "deposits",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/OutletDepositsTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.pricelists.root,
            path: "pricelists",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewoutlet/pricing/PriceListTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.finance,
            path: "finances",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewoutlet/finance/FinanceTab.vue"
              ),
          },
          {
            name: contactsMenu.outlets.settings,
            path: "settings",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewoutlet/settings/SettingsTab.vue"
              ),
          },
        ],
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.outletGroups.list,
        icon: "List",
        url: "/groups/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/ListGroups.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.outletGroups.create,
        icon: "Create",
        url: "/groups/create",
        breadcrumbParentName: contactsMenu.outletGroups.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/CreateOutletGroupWizard.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: "",
        icon: "Edit",
        url: "/groups/:id",
        componentProps: (route) => ({
          idString: route.params.id,
        }),
        breadcrumbParentName: contactsMenu.outletGroups.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "outlet" */ "@/components/pages/outlet/ViewGroup.vue"
          ),
        visible: false,
        routeMetadata: {
          routeKey: (route) => "/groups/" + route.params.id,
        },
        childRoutes: [
          {
            name: contactsMenu.outletGroups.contacts,
            path: "contacts",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/contacts/ContactsTab.vue"
              ),
          },
          {
            name: contactsMenu.outletGroups.documents,
            path: "documents",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/DocumentStoreTab.vue"
              ),
          },
          {
            name: contactsMenu.outletGroups.outlets,
            path: "outlets",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewgroup/GroupOutletsTab.vue"
              ),
          },
          {
            name: contactsMenu.outletGroups.pricing,
            path: "/groups/:id/pricing",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewgroup/GroupPricingTab.vue"
              ),
          },
          {
            name: contactsMenu.outletGroups.finance,
            path: "/groups/:id/finance",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewgroup/GroupFinanceTab.vue"
              ),
          },
          {
            name: contactsMenu.outletGroups.settings,
            path: "/groups/:id/settings",
            component: () =>
              import(
                /* webpackChunkName: "outlet" */ "@/components/pages/outlet/viewgroup/GroupSettingsTab.vue"
              ),
          },
        ],
        requiredUserPermissions: [EnumUserPermission.outletsAndGroupsView],
      }),
      MenuItem.fromOpts({
        name: contactsMenu.suppliers.list,
        url: "/stock/suppliers/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/suppliers/ListSuppliers.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: contactsMenu.suppliers.view,
        url: "/stock/suppliers/:id/:mode",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/suppliers/EditSupplier.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          id: route.params.id,
          mode: route.params.mode as "view" | "edit",
        }),
        routeMetadata: {
          routeKey: (route) => "/stock/suppliers/" + route.params.id,
        },
      }),
      MenuItem.fromOpts({
        name: contactsMenu.suppliers.create,
        url: "/stock/suppliers/create",
        breadcrumbParentName: contactsMenu.suppliers.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/suppliers/EditSupplier.vue"
          ),
        visible: false,
        componentProps: () => ({
          mode: "create" as const,
        }),
      }),

      MenuItem.fromOpts({
        name: contactsMenu.communications.list,
        url: "/communications/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/outlet/communications/ListCommunications.vue"
          ),
      }),
    ],
  }),
];
